import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FishBuilder from '../../components/FishBuilder';
import Loading from '../../components/Loading';
import TranslatableText, { speak } from '../../components/TranslatableText';
import ClassGoals from '../../components/ClassGoals';
import BeaconGuide from '../../components/BeaconGuide';
import InventoryPanel, { HOTBAR_SIZE, place } from '../../components/InventoryPanel';
import TouchControls, { useTouch } from '../../components/TouchControls';
import Fishpedia from '../../components/Fishpedia';
import NpcDialog from '../../components/NpcDialog';
import OceanQuiz from '../../components/OceanQuiz';
import { untilChange } from '../../game/daynight';
import type { NpcId } from '../../game/npcs';
import { cleanLook, randomLook, type Look } from '../../game/avatar';
import { BLOCKS, PLACEABLE, blockByItem } from '../../game/blocks';
import HouseRoom from '../../components/HouseRoom';
import { BEASTS, type BeastKind } from '../../game/beasts';
import { setMood, setSfxMuted, sfxMuted, startAmbience, stopAmbience } from '../../game/sfx';
import { nightness } from '../../game/daynight';
import HabitatPanel, { RarityTag } from '../../components/HabitatPanel';
import { BUCKET_SIZE, Ocean as Engine, type OceanData, type Target, type Tool } from '../../game/engine';
import { HOUSES, isHouse, type HouseKind } from '../../game/houses';
import { OceanLive } from '../../game/live';
import { englishSigns, fishName, oceanWords, areaNameEn } from '../../game/lang';
import { worldFor, type AreaId } from '../../game/world';
import { RARITY, SPECIES, speciesById, speciesImage } from '../../lib/aquarium';
import { saveCard, termKey } from '../../lib/cards';
import {
  attractFish, catchFish, chestQuestion, craft, decorateHouse, doQuest, faint, goalStatus, openChest, recordDefeat, type Goal, fetchDecorPrices, useWeapon, fetchOcean, fetchRecipes, fetchTiles, mineBlock, moveFish, pickUpHouse, placeBlock,
  placeHouse, recordFind, releaseFish, releaseResident, saveLook, type Recipe,
} from '../../lib/ocean';
import { Link } from 'react-router-dom';
import { useClassroom } from '../../state/Classroom';
import { sandDollars } from '../../lib/shop';

// ─────────────────────────────────────────────────────────────────────────────
// The class ocean, for a student: a shared sandbox. Swim as your own fish,
// catch wild fish with a net (Space), mine with a pickaxe and build with a
// hammer (bought with sand dollars; they wear out), craft, buy fish houses for
// your spot, find pearls and hidden areas, and read signs in the language
// you're learning. Sand dollars come from the teacher.
// The game is src/game/engine.ts; this page is the HUD and the saving.
// ─────────────────────────────────────────────────────────────────────────────

type Toast = { id: number; title: string; text?: string; lang?: boolean; bad?: boolean; /** Something to hear (a fish's name in the class's language). */ say?: string };
type Quiz = { kind: 'chest'; id: string; tier: 1 | 2 | 3; term: string; lang: string; options: string[] } | { kind: 'gate'; i: number; options: string[] };
const EMOTES = ['👋', '❤️', '😂', '😮', '👍', '🐟', '🎉', '😱'];
type Slot = { key: string; tool: Tool; icon: string; label: string; count?: number; color?: string };

const ITEM_NAMES: Record<string, string> = {
  pearl: 'Pearl', drill: 'Drill', golden_net: 'Golden net', pickaxe: 'Pickaxe', hammer: 'Hammer', sword: 'Sword', trident: 'Trident',
  ...Object.fromEntries(Object.values(BLOCKS).map(b => [b.item, b.name])),
  ...Object.fromEntries(Object.entries(HOUSES).map(([k, h]) => [k, h.name])),
};
const itemName = (item: string) => ITEM_NAMES[item] ?? item.replace(/_/g, ' ');
const TOOLS = new Set(['pickaxe', 'hammer', 'sword', 'trident']);
const BEAST_ICON: Record<BeastKind, string> = { shark: '🦈', eel: '🐍', jelly: '🪼', angler: '🐡', serpent: '🐉', crab: '🦀', kraken: '🐙' };
const BEAST_AREAS = new Set<string>(['caves', 'grotto', 'echo', 'hollow', 'trench', 'temple', 'abyss']);
/** Ask before letting a fish go: it's for good. */

const ITEM_ICON: Record<string, string> = { beacon: '🐠', pearl: '🫧', drill: '🔩', golden_net: '🥅', pickaxe: '⛏️', hammer: '🔨', sword: '⚔️', trident: '🔱', shell_hut: '🐚', coral_cottage: '🏠', treasure_chest: '🧰' };

export default function OceanPage() {
  const { active, hydrated, session, cards, setCards } = useClassroom();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const miniRef = useRef<HTMLCanvasElement>(null);
  const engine = useRef<Engine | null>(null);
  const [ready, setReady] = useState(0);
  const [data, setData] = useState<OceanData | null>(null);
  const dataRef = useRef<OceanData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<Target>(null);
  const [area, setArea] = useState<AreaId | null>(null);
  const [areas, setAreas] = useState<Set<string>>(new Set());
  const [pearls, setPearls] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [sign, setSign] = useState<number | null>(null);
  const [builder, setBuilder] = useState(false);
  const [panel, setPanel] = useState<null | 'craft' | 'bucket' | 'inv'>(null);
  const [invTarget, setInvTarget] = useState<number | null>(null);
  const [guide, setGuide] = useState(false);   // the Fish Beacon guide
  // "Let it go?": asked on the page, not with window.confirm, which throws the browser out of full screen.
  const [letGoAsk, setLetGoAsk] = useState<{ species: string; go: () => void } | null>(null);
  const letGo = (species: string, go: () => void) => setLetGoAsk({ species, go });
  const panelRef = useRef(panel); panelRef.current = panel;   // an empty hotbar slot clicked: fill it from the inventory
  const [roomId, setRoomId] = useState<string | null>(null);
  const [beacon, setBeacon] = useState<{ tx: number; ty: number } | null>(null);
  const [decorPrices, setDecorPrices] = useState<Record<string, number>>({});
  const [lives, setLives] = useState(3);
  const [muted, setMuted] = useState(sfxMuted);
  const [pediaOpen, setPediaOpen] = useState(false);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [npc, setNpc] = useState<NpcId | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [emoteOpen, setEmoteOpen] = useState(false);
  const [boss, setBoss] = useState<{ name: string; hp: number; max: number } | null>(null);
  const [sky, setSky] = useState(untilChange);
  const touch = useTouch();
  const onStick = useCallback((x: number, y: number) => engine.current?.setStick(x, y), []);
  const roomOpen = useRef(false); roomOpen.current = roomId !== null || pediaOpen || quiz !== null || npc !== null || goalsOpen;
  const [toolKey, setToolKey] = useState('net');
  // Full screen (students asked): the browser's own where it has one, otherwise (iPhone) the page fills the window.
  const pageRef = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  const toggleFull = () => {
    const el = pageRef.current;
    if (document.fullscreenElement) { void document.exitFullscreen(); return; }
    if (full) { setFull(false); return; }
    if (el?.requestFullscreen) el.requestFullscreen().then(() => setFull(true)).catch(() => setFull(true));
    else setFull(true);
    canvasRef.current?.focus();
  };
  useEffect(() => {
    const sync = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [balance, setBalance] = useState(10);
  const [inv, setInv] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(() => { try { return localStorage.getItem('ocean.sandbox.hinted') !== '1'; } catch { return true; } });
  const lastTile = useRef<string | null>(null);
  const live = useRef<OceanLive | null>(null);
  const [nearby, setNearby] = useState(0);

  const cid = active?.id;
  const world = useMemo(() => worldFor(active?.ocean_seed), [active?.ocean_seed]);
  const lang = active?.language ?? 'es';
  const native = active?.native_language ?? 'en';
  const words = oceanWords(lang);
  const me = data?.me ?? null;
  const look: Look | null = useMemo(() => (me?.look ? cleanLook(me.look) : null), [me?.look]);

  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-2), { ...t, id }]);
    window.setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), t.bad ? 4000 : 5500);
  }, []);

  // This week's class goals (refreshed now and then, and after things that count).
  const refreshGoals = useCallback(async () => {
    if (!cid) return;
    try { setGoals((await goalStatus(cid)).goals); } catch { /* not run yet, or offline */ }
  }, [cid]);
  useEffect(() => {
    void refreshGoals();
    const id = window.setInterval(() => { if (!document.hidden) void refreshGoals(); }, 60000);
    return () => window.clearInterval(id);
  }, [refreshGoals]);

  /** Open a chest (with the answer to its word question, if it asked one) and show what was inside. */
  const openTheChest = useCallback(async (id: string, tier: 1 | 2 | 3, answer?: string) => {
    if (!cid) return;
    try {
      const r = await openChest(cid, id, answer);
      engine.current?.markChest(id);
      setInv(v => { const n = { ...v }; for (const l of r.loot) n[l.item] = (n[l.item] ?? 0) + l.qty; return n; });
      const what = r.loot.map(l => `${ITEM_ICON[l.item] ?? (blockByItem(l.item) ? '▪️' : '•')} ${itemName(l.item)} × ${l.qty}`).join('  ');
      toast({
        title: `${tier === 3 ? '👑' : '🧰'} ${r.correct ? 'Correct! Bonus treasure!' : tier === 3 ? 'The Kraken’s treasure!' : 'Treasure!'}`,
        text: r.correct === false && r.answer ? `It meant “${r.answer}”. ${what}` : what,
      });
      void refreshGoals();
    } catch (err) { toast({ title: err instanceof Error ? err.message : 'It’s stuck. Try again.', bad: true }); }
  }, [cid, toast, refreshGoals]);

  const reload = useCallback(async () => {
    if (!cid) return;
    try {
      const d = await fetchOcean(cid);
      dataRef.current = d;
      setData(d);
      setAreas(new Set(d.me?.areas ?? []));
      setPearls(new Set(d.me?.pearls ?? []));
      setBalance(d.me?.balance ?? 10);
      setInv(d.me?.inventory ?? {});
      engine.current?.setData(d);
      setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the ocean.'); }
  }, [cid]);

  // The class's blocks: everything once, then just what changed every few seconds,
  // and everything again now and then (so the teacher's undos show up too).
  const syncTiles = useCallback(async (full: boolean) => {
    if (!cid || !engine.current) return;
    try {
      const edits = await fetchTiles(cid, full ? undefined : lastTile.current ?? undefined);
      if (edits.length) lastTile.current = edits[edits.length - 1].updated_at ?? lastTile.current;
      engine.current.setEdits(edits, full);
    } catch { /* try again next time */ }
  }, [cid]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const e = new Engine(canvasRef.current, 'play', world);
    engine.current = e;
    e.setMinimap(miniRef.current);
    if (dataRef.current) e.setData(dataRef.current);
    setReady(r => r + 1);
    return () => { e.destroy(); engine.current = null; };
  }, [hydrated, cid, world, data?.locked]);

  useEffect(() => { void reload(); fetchRecipes().then(setRecipes).catch(() => {}); fetchDecorPrices().then(setDecorPrices).catch(() => {}); }, [reload]);
  useEffect(() => {
    if (!ready) return;
    lastTile.current = null;
    void syncTiles(true);
    let n = 0;
    const id = window.setInterval(() => { if (!document.hidden) void syncTiles(++n % 12 === 0); }, 5000);
    return () => window.clearInterval(id);
  }, [ready, syncTiles]);

  // Live: classmates in the ocean right now, and their digging and building as it happens.
  const liveInfo = useRef<{ look: unknown }>({ look: null });
  liveInfo.current = { look: me?.look ?? null };
  useEffect(() => {
    const e = engine.current, uid = session?.user.id;
    if (!e || !cid || !uid) return;
    const l = new OceanLive(cid, uid, world.w, {
      onPeer: m => engine.current?.peerUpdate(m),
      onLeave: id => engine.current?.peerLeft(id),
      onTile: t => engine.current?.setEdits([t], false),
      onBeasts: m => engine.current?.beastState(m),
      onBeastEvent: ev => engine.current?.beastEvent(ev),
      onEmote: (id, em) => engine.current?.emote(id, em),
    });
    live.current = l;
    e.setMe(uid);
    const id = window.setInterval(() => {
      const eng = engine.current;
      if (!eng) return;
      // Only once you have a fish, and not while the tab is in the background.
      if (liveInfo.current.look && !document.hidden) l.tick(performance.now(), eng.pose(), () => liveInfo.current);
      setNearby(eng.peerCount());
      const b = eng.boss();
      setBoss(prev => (!b && !prev) || (b && prev && b.hp === prev.hp && b.max === prev.max) ? prev : b);
      setMood(eng.mood(), nightness());
    }, 100);
    const bye = () => l.leave();
    window.addEventListener('pagehide', bye);
    return () => { window.clearInterval(id); window.removeEventListener('pagehide', bye); l.leave(); live.current = null; };
  }, [ready, cid, session?.user.id, world]);

  // Everything you can hold: net, pickaxe, weapon, then whatever blocks and houses you have (all of it in the inventory).
  const items: Slot[] = useMemo(() => {
    const s: Slot[] = [
      { key: 'net', tool: { kind: 'net' }, icon: inv.golden_net ? '🥅' : '🪝', label: inv.golden_net ? 'Golden net' : 'Net' },
      { key: 'pick', tool: { kind: 'pick' }, icon: '⛏️', label: (inv.pickaxe ?? 0) > 0 ? `Pickaxe: ${inv.pickaxe} uses left${inv.drill ? ' (with Drill)' : ''}` : 'Pickaxe (buy one in the shop)', count: inv.pickaxe ?? 0 },
    ];
    const blades = (inv.sword ?? 0) + (inv.trident ?? 0);
    if (blades > 0) {
      const tri = (inv.trident ?? 0) > 0;
      s.push({ key: 'sword', tool: { kind: 'sword' }, icon: tri ? '🔱' : '⚔️', label: `${tri ? 'Trident' : 'Sword'}: ${tri ? inv.trident : inv.sword} hits left`, count: tri ? inv.trident : inv.sword });
    }
    for (const item of PLACEABLE) if ((inv[item] ?? 0) > 0) {
      const def = blockByItem(item)!;
      s.push({ key: item, tool: { kind: 'block', item }, icon: '', label: def.name, count: inv[item], color: def.color });
    }
    for (const kind of Object.keys(HOUSES) as HouseKind[]) if ((inv[kind] ?? 0) > 0) {
      s.push({ key: kind, tool: { kind: 'house', item: kind }, icon: ITEM_ICON[kind], label: `${HOUSES[kind].name} (place it on the sea floor)`, count: inv[kind] });
    }
    return s;
  }, [inv]);

  // The hotbar: 9 slots the student arranges in the inventory, saved in this browser per class. A slot keeps
  // its item even while you have none of it (it comes back when you get more); new kinds of items fill empty slots.
  const [hotbar, setHotbar] = useState<(string | null)[]>(() => Array(HOTBAR_SIZE).fill(null));
  const hotbarReady = useRef(false);
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!cid || !ready) return;
    let saved: (string | null)[] | null = null;
    try { const v = JSON.parse(localStorage.getItem(`ocean.hotbar.${cid}`) ?? 'null'); if (Array.isArray(v) && v.length === HOTBAR_SIZE) saved = v.map(x => (typeof x === 'string' ? x : null)); } catch { /* fine */ }
    if (saved) { setHotbar(saved); for (const it of items) seen.current.add(it.key); }
    hotbarReady.current = true;
  }, [cid, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hotbarReady.current) return;
    setHotbar(h => {
      const next = [...h];
      for (const it of items) {
        if (seen.current.has(it.key)) continue;
        seen.current.add(it.key);
        if (next.includes(it.key)) continue;
        const free = next.indexOf(null);
        if (free >= 0) next[free] = it.key;
      }
      return next.every((k, i) => k === h[i]) ? h : next;
    });
  }, [items, ready]);
  useEffect(() => {
    if (!hotbarReady.current || !cid) return;
    try { localStorage.setItem(`ocean.hotbar.${cid}`, JSON.stringify(hotbar)); } catch { /* fine */ }
  }, [hotbar, cid]);
  const slots = hotbar.map(k => (k ? items.find(i => i.key === k) ?? null : null));
  const current = items.find(s => s.key === toolKey) ?? items[0];
  const offBar = items.filter(i => !hotbar.includes(i.key)).length;

  useEffect(() => { engine.current?.setLook(look); }, [look, ready]);
  const modal = roomId !== null || beacon !== null || pediaOpen || quiz !== null || npc !== null || goalsOpen;
  useEffect(() => { engine.current?.setPaused(modal); }, [modal, ready]);
  // The parts of the minimap uncovered before, in this browser.
  useEffect(() => {
    if (!ready || !cid) return;
    try { engine.current?.setRevealed(JSON.parse(localStorage.getItem(`ocean.map.${cid}`) ?? '[]')); } catch { /* fine */ }
  }, [ready, cid]);
  // Lives carry over a reload (so refreshing isn't a free heal), healing one per 30 s since.
  useEffect(() => {
    if (!ready || !cid) return;
    try {
      const saved = JSON.parse(localStorage.getItem(`ocean.lives.${cid}`) ?? 'null') as { n: number; at: number } | null;
      if (saved && saved.n < 3) engine.current?.setLives(saved.n + Math.floor((Date.now() - saved.at) / 30000));
    } catch { /* fine */ }
  }, [ready, cid]);
  useEffect(() => { engine.current?.setTool(current.tool); }, [current, ready]);
  useEffect(() => {
    engine.current?.setGear({ drill: (inv.drill ?? 0) > 0, goldenNet: (inv.golden_net ?? 0) > 0, pickaxe: inv.pickaxe ?? 0, hammer: inv.hammer ?? 0, sword: inv.sword ?? 0, trident: inv.trident ?? 0 });
  }, [inv, ready]);
  // A block or house you ran out of drops back to the pickaxe or net.
  useEffect(() => { if (!items.some(s => s.key === toolKey)) setToolKey(isHouse(toolKey) ? 'net' : 'pick'); }, [items, toolKey]);
  const bucketCount = data?.me?.bucket.length ?? 0;
  useEffect(() => { engine.current?.setBucket(bucketCount); }, [bucketCount, ready]);

  // The ocean's ambience starts on the first key or tap (browsers need one before sound), and stops on leaving.
  useEffect(() => {
    const go = () => { if (!sfxMuted()) startAmbience(); };
    window.addEventListener('keydown', go);
    window.addEventListener('pointerdown', go);
    const id = window.setInterval(() => setSky(untilChange()), 15000);
    return () => { window.removeEventListener('keydown', go); window.removeEventListener('pointerdown', go); window.clearInterval(id); stopAmbience(); };
  }, []);

  // Number keys pick a hotbar slot; C opens crafting, B the bucket.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) return;
      if (roomOpen.current) return;
      if (panelRef.current === 'inv') return;                // the inventory handles its own keys
      if (e.key.toLowerCase() === 'i' && !e.repeat) { setInvTarget(null); setPanel('inv'); return; }
      if (e.key === 'Escape' && !document.fullscreenElement && !panelRef.current) setFull(false);   // leaving the window-filling full screen
      if (e.key === 'Escape') { setPanel(null); setSign(null); setBeacon(null); setToolKey(k => (isHouse(k) ? 'net' : k)); return; }
      if (/^[1-9]$/.test(e.key)) { const s = slots[Number(e.key) - 1]; if (s) setToolKey(s.key); }
      if (e.key.toLowerCase() === 'c' && !e.repeat) setPanel(p => (p === 'craft' ? null : 'craft'));
      if (e.key.toLowerCase() === 'b' && !e.repeat) setPanel(p => (p === 'bucket' ? null : 'bucket'));
      if (e.key.toLowerCase() === 'p' && !e.repeat) setPediaOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slots]);

  // What the engine reports.
  const invRef = useRef(inv); invRef.current = inv;
  useEffect(() => {
    const e = engine.current;
    if (!e || !cid) return;
    e.events = {
      onTarget: setTarget,
      onArea: (id, isNew) => {
        setArea(id);
        if (BEAST_AREAS.has(id)) {
          try {
            if (sessionStorage.getItem('ocean.beasts.warned') !== '1') {
              sessionStorage.setItem('ocean.beasts.warned', '1');
              toast({ title: '⚠️ Sea beasts live down here!', text: (invRef.current.sword ?? 0) + (invRef.current.trident ?? 0) > 0 ? 'Pick your sword (on your hotbar) and swing with Space.' : 'Sharks, eels and stinging jellies bite. A sword from the shop (C) lets you fight back.', bad: true });
            }
          } catch { /* fine */ }
        }
        if (!isNew) return;
        setAreas(prev => new Set(prev).add(id));
        toast({ title: 'Discovered!', text: words.areas[id], lang: true });
        void recordFind(cid, 'area', id).catch(() => {});
      },
      onPearl: id => {
        setPearls(prev => {
          const next = new Set(prev).add(id);
          toast({ title: `🫧 Pearl found! ${next.size} of ${world.pearls.length}`, text: 'Pearls are used for crafting.' });
          return next;
        });
        setInv(v => ({ ...v, pearl: (v.pearl ?? 0) + 1 }));
        void recordFind(cid, 'pearl', id).catch(() => {});
      },
      onInteract: t => {
        if (!t) return;
        if (t.kind === 'house') setRoomId(t.id);
        else if (t.kind === 'beacon') setBeacon({ tx: t.tx, ty: t.ty });
        else if (t.kind === 'chest') void (async () => {
          // Temple and Abyss chests ask a word from your flashcards first.
          const q = t.tier > 1 ? await chestQuestion(cid, t.id).catch(() => null) : null;
          if (q) setQuiz({ kind: 'chest', id: t.id, tier: t.tier, ...q });
          else await openTheChest(t.id, t.tier);
        })();
        else if (t.kind === 'gate') {
          // The inscription's meaning, and three that aren't.
          const en = englishSigns(), right = en[10 + t.i];
          const others = [3, 5, 8, 9, 2, 4].map(k => en[k]).filter(x => x && x !== right).slice(0, 3);
          setQuiz({ kind: 'gate', i: t.i, options: [right, ...others].sort(() => Math.random() - 0.5) });
        }
        else if (t.kind === 'npc') setNpc(t.id);
        else setSign(t.i);
      },
      onNotice: text => toast({ title: text, bad: true }),
      onMine: async (tx, ty, item) => {
        const prev = blockByItem(item)?.id ?? 0;
        // Each block wears the pickaxe down by one use.
        setInv(v => ({ ...v, [item]: (v[item] ?? 0) + 1, pickaxe: Math.max(0, (v.pickaxe ?? 1) - 1) }));
        try {
          const r = await mineBlock(cid, tx, ty, item);
          live.current?.sendTile({ tx, ty, tile: 0 });
          setInv(v => ({ ...v, pickaxe: r.pickaxe }));
          if (r.pickaxe === 0) toast({ title: '💥 Your pickaxe broke!', text: 'Buy a new one in the shop (C) to keep mining.' });
        } catch (err) {
          e.revert(tx, ty, prev);
          setInv(v => ({ ...v, [item]: Math.max(0, (v[item] ?? 1) - 1), pickaxe: (v.pickaxe ?? 0) + 1 }));
          toast({ title: err instanceof Error ? err.message : 'Could not mine that.', bad: true });
        }
      },
      onBuild: async (tx, ty, item) => {
        if ((invRef.current[item] ?? 0) < 1) { e.revert(tx, ty, 0); return; }
        invRef.current = { ...invRef.current, [item]: invRef.current[item] - 1 };
        // Each block placed wears the hammer down by one use.
        setInv(v => ({ ...v, [item]: Math.max(0, (v[item] ?? 1) - 1), hammer: Math.max(0, (v.hammer ?? 1) - 1) }));
        try {
          const r = await placeBlock(cid, tx, ty, item);
          live.current?.sendTile({ tx, ty, tile: blockByItem(item)?.id ?? 0 });
          setInv(v => ({ ...v, hammer: r.hammer }));
          if (r.hammer === 0) toast({ title: '💥 Your hammer broke!', text: 'Buy a new one in the shop (C) to keep building.' });
        } catch (err) {
          e.revert(tx, ty, 0);
          setInv(v => ({ ...v, [item]: (v[item] ?? 0) + 1, hammer: (v.hammer ?? 0) + 1 }));
          toast({ title: err instanceof Error ? err.message : 'Could not build there.', bad: true });
        }
      },
      onCatch: async species => {
        try {
          await catchFish(cid, species);
          const sp = speciesById(species);
          const local = fishName(sp.id, sp.name, lang);
          toast({ title: `🎣 ${lang.startsWith('es') ? `¡${local}!` : `${local}!`}`, text: `${local === sp.name ? '' : `${sp.name} · `}${RARITY[sp.rarity].name} · in your bucket (B)`, say: local });
          void refreshGoals();
          void reload();
        } catch (err) {
          toast({ title: err instanceof Error ? err.message : 'It got away!', bad: true });
          void reload();
        }
      },
      onLives: (n, by) => {
        setLives(n);
        try { localStorage.setItem(`ocean.lives.${cid}`, JSON.stringify({ n, at: Date.now() })); } catch { /* fine */ }
        if (by && n > 0) toast({ title: `${BEAST_ICON[by]} ${by === 'jelly' ? 'A jelly stung you!' : `A ${BEASTS[by].name} bit you!`} ${'❤️'.repeat(n)} left`, text: n === 1 ? 'One more and you’re sent back to the start. Swim up to safety!' : undefined, bad: true });
      },
      onFaint: async by => {
        try {
          const r = await faint(cid);
          setBalance(r.balance);
          toast({ title: `${BEAST_ICON[by]} The ${BEASTS[by].name} got you!`, text: `You woke up back at the start of the ocean${r.lost ? ` and lost 🪙 ${r.lost}` : ''}.`, bad: true });
        } catch { toast({ title: `${BEAST_ICON[by]} The ${BEASTS[by].name} got you!`, text: 'You woke up back at the start of the ocean.', bad: true }); }
      },
      onWeaponHit: async weapon => {
        setInv(v => ({ ...v, [weapon]: Math.max(0, (v[weapon] ?? 1) - 1) }));
        try {
          const left = await useWeapon(cid, weapon);
          setInv(v => ({ ...v, [weapon]: left }));
          if (left === 0) toast({ title: `💥 Your ${weapon} broke!`, text: 'Buy a new one in the shop (C).' });
        } catch { /* the hit still counts on screen */ }
      },
      onHabitatVisit: async (tx, ty) => {
        try {
          const r = await attractFish(cid, tx, ty);
          setData(d => {
            if (!d) return d;
            const others = (d.habitats ?? []).filter(h => h.tx !== tx || h.ty !== ty);
            const mine = (d.habitats ?? []).find(h => h.tx === tx && h.ty === ty);
            const next = { ...d, habitats: [...others, { tx, ty, owner: mine?.owner ?? null, mine: mine?.mine ?? false, residents: r.residents }] };
            dataRef.current = next;
            engine.current?.setData(next);
            return next;
          });
          if (r.arrived) {
            const sp = speciesById(r.arrived);
            toast({ title: `🏡 A ${sp.name} moved in!`, text: `${RARITY[sp.rarity].name} · it likes what you built.` });
          }
        } catch { /* try again on the next visit */ }
      },
      onReveal: i => {
        try {
          const key = `ocean.map.${cid}`;
          const seen = new Set<number>(JSON.parse(localStorage.getItem(key) ?? '[]'));
          seen.add(i);
          localStorage.setItem(key, JSON.stringify([...seen]));
        } catch { /* fine */ }
      },
      onRareFish: species => {
        const sp = speciesById(species);
        toast({ title: `✨ Something ${RARITY[sp.rarity].name.toLowerCase()} is nearby…`, text: `A ${sp.name}! It’s shy: swim close slowly and catch it before it bolts.` });
      },
      onBoss: () => toast({ title: '🐙 The Kraken awakens!', text: 'It grows stronger with every swimmer here: fight it together!', bad: true }),
      onBeastDied: kind => { void recordDefeat(cid, kind).then(() => refreshGoals()).catch(() => {}); },
      onBeastDefeated: kind => toast({ title: `⚔️ You fought off the ${BEASTS[kind].name}!` }),
      // Sea beasts are shared by everyone swimming in the same part of the ocean.
      onBeastSync: m => live.current?.sendBeasts(m),
      onBeastEvent: ev => live.current?.sendBeastEvent(ev),
      onPlaceHouse: async (kind, x, y) => {
        try {
          await placeHouse(cid, kind, x, y);
          setToolKey('net');
          toast({ title: `${HOUSES[kind].name} placed! 🏡`, text: 'Move fish in from your bucket (B).' });
          await reload();
        } catch (err) { toast({ title: err instanceof Error ? err.message : 'Could not place it.', bad: true }); }
      },
    };
  }, [cid, words, toast, reload, ready, world, lang, openTheChest, refreshGoals]);

  // Saving words from signs to flashcards, like videos and stories.
  const savedKeys = useMemo(() => new Set(cards.map(c => termKey(c.term))), [cards]);
  const onSave = useCallback(async (term: string, translation: string) => {
    const card = await saveCard({ term, translation, sourceLang: lang, targetLang: native, classroomId: cid });
    setCards(prev => [card, ...prev]);
  }, [lang, native, cid, setCards]);

  if (!hydrated) return <Loading />;
  if (!active) return <div className="notice">Join a class to explore its ocean.</div>;
  // Homework first (unless the teacher opened it for free): ocean_world says so.
  if (data?.locked) return (
    <div className="narrow">
      <div className="panel ocean-locked">
        <div className="ocean-locked-icon" aria-hidden>🔒🌊</div>
        <h2>The ocean opens after homework</h2>
        <p className="muted">Finish at least one assignment today (a lesson, a video or a story) and the ocean is yours for the rest of the day. All your homework done? Then it’s open too.</p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <Link className="button primary" to="/work">See my work →</Link>
          <button type="button" onClick={() => void reload()}>I did one: check again</button>
        </div>
      </div>
    </div>
  );

  const run = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await f(); if (ok) toast({ title: ok }); await reload(); }
    catch (err) { toast({ title: err instanceof Error ? err.message : 'That didn’t work.', bad: true }); }
    finally { setBusy(false); }
  };

  const actionLabel = !target ? null
    : target.kind === 'house' ? '🏠 Go inside'
      : target.kind === 'chest' ? '🧰 Open the chest'
        : target.kind === 'beacon' ? '🐠 Look at this habitat'
      : '📜 Read';

  const myHouses = data?.houses.filter(h => h.mine) ?? [];
  const room = roomId ? data?.houses.find(h => h.id === roomId) ?? null : null;
  const bucket = me?.bucket ?? [];
  const has = (inputs: Record<string, number>) => Object.entries(inputs).every(([k, n]) => (inv[k] ?? 0) >= n);
  const goHome = () => {
    const e = engine.current, h = e?.myHome();
    if (e && h) e.goTo(h.x, h.y);
    canvasRef.current?.focus();
  };

  return (
    <div className={`ocean-page${full ? ' full' : ''}`} ref={pageRef}>
      <div className={`ocean-stage${touch ? ' touch' : ''}`}>
        <canvas ref={canvasRef} className="ocean-canvas" tabIndex={0} aria-label="The class ocean. Arrow keys or hold to swim; Space to swing your net; click blocks to mine or build; E to interact." />

        <div className="ocean-hud top-left">
          <Link to="/ocean" className="ocean-chip" title="Back to all the games">← All games</Link>
          <div className="ocean-chip sand" title="Sand dollars: from your teacher, for tools, crafting and the shop">🪙 {sandDollars(balance)}</div>
          <div className={`ocean-chip hearts${lives === 1 ? ' low' : ''}`} title="Lives: sea beasts in the deep take one per bite, and one comes back every 30 s. Lose all three and you go back to the start (you keep your sand dollars)." aria-label={`${lives} of 3 lives`}>
            {[0, 1, 2].map(i => <span key={i} aria-hidden>{i < lives ? '❤️' : '🖤'}</span>)}
          </div>
          <div className={`ocean-chip tool${(inv.pickaxe ?? 0) > 0 ? '' : ' empty'}`} title="Pickaxe uses left (each block mined uses one)">⛏️ {inv.pickaxe ?? 0}</div>
          <div className={`ocean-chip tool${(inv.hammer ?? 0) > 0 ? '' : ' empty'}`} title="Hammer uses left (each block placed uses one)">🔨 {inv.hammer ?? 0}</div>
          {area && <div className="ocean-chip" dir="auto" title={areaNameEn(area)}>🗺 {words.areas[area]}</div>}
          {nearby > 0 && <div className="ocean-chip" title="Classmates swimming in this part of the ocean right now">👥 {nearby} {nearby === 1 ? 'classmate' : 'classmates'} nearby</div>}
          <div className="ocean-chip" title={sky.night ? 'Night: deep-sea fish rise toward the surface' : 'Day'}>{sky.night ? '🌙' : '☀️'} {sky.night ? 'Night' : 'Day'} · {sky.minutes} min</div>
        </div>

        {boss && (
          <div className="ocean-hud top-center boss-bar" role="status" aria-label={`${boss.name}: ${boss.hp} of ${boss.max}`}>
            <strong>🐙 {boss.name}</strong>
            <div className="boss-track"><span style={{ width: `${(boss.hp / boss.max) * 100}%` }} /></div>
            <span className="small">{boss.hp}/{boss.max}</span>
          </div>
        )}

        <div className="ocean-hud top-right">
          <canvas ref={miniRef} className="ocean-mini" aria-label="Map" />
          <div className="ocean-stats">
            <span title="Pearls found">🫧 {pearls.size}/{world.pearls.length}</span>
            <span title="Areas discovered">🧭 {areas.size}/{world.areaIds.length}</span>
          </div>
        </div>

        <div className="ocean-hud bottom-left">
          {myHouses.length > 0 && <button type="button" onClick={goHome} title="Swim to your newest fish house" aria-label="Home">🏠<span className="lbl"> Home</span></button>}
          <button type="button" onClick={() => setPanel('bucket')} aria-label="Bucket">🪣<span className="lbl"> Bucket</span>{bucket.length ? <span className={`count${bucket.length >= BUCKET_SIZE ? ' full' : ''}`}>{bucket.length}/{BUCKET_SIZE}</span> : null}</button>
          <button type="button" onClick={() => setPanel('craft')} aria-label="Craft and shop">🛠<span className="lbl"> Craft & shop</span></button>
          <button type="button" onClick={() => setPediaOpen(true)} title="Fishpedia (P)" aria-label="Fishpedia">📖<span className="lbl"> {(me?.pedia ?? []).length}/{SPECIES.length}</span></button>
          <button type="button" onClick={() => { setGoalsOpen(true); void refreshGoals(); }} title="This week’s class goals">🎯{goals.length ? <span className="count">{goals.filter(g => g.progress >= g.target).length}/{goals.length}</span> : null}</button>
          <span className="emote-wrap">
            <button type="button" onClick={() => setEmoteOpen(o => !o)} aria-label="Emotes" aria-expanded={emoteOpen}>😀</button>
            {emoteOpen && (
              <span className="emote-picker" role="menu">
                {EMOTES.map(em => (
                  <button key={em} type="button" role="menuitem" onClick={() => { engine.current?.emote('me', em); live.current?.sendEmote(em); setEmoteOpen(false); canvasRef.current?.focus(); }}>{em}</button>
                ))}
              </span>
            )}
          </span>
          <button type="button" onClick={() => setBuilder(true)} aria-label="Change my fish">🎨</button>
          <button type="button" onClick={() => { setSfxMuted(!muted); setMuted(!muted); if (muted) startAmbience(); canvasRef.current?.focus(); }} aria-label={muted ? 'Turn sounds on' : 'Turn sounds off'} title={muted ? 'Sounds are off' : 'Sounds are on'}>{muted ? '🔇' : '🔊'}</button>
          <button type="button" onClick={toggleFull} aria-label={full ? 'Leave full screen' : 'Full screen'} title={full ? 'Leave full screen (Esc)' : 'Full screen'}>{full ? '🗗' : '⛶'}</button>
        </div>

        {touch && look && !modal && !hint && (
          <TouchControls onStick={onStick} onAction={() => engine.current?.act()}
            actionIcon={current.tool.kind === 'net' ? '🎣' : current.tool.kind === 'sword' ? '⚔️' : current.tool.kind === 'pick' ? '⛏️' : current.tool.kind === 'house' ? '🏠' : '🧱'}
            actionLabel={current.tool.kind === 'net' ? 'Swing your net' : current.tool.kind === 'sword' ? 'Swing your sword' : current.tool.kind === 'pick' ? 'Mine in front' : 'Build in front'} />
        )}

        <div className="ocean-hud bottom-center hotbar-wrap">
          {actionLabel && <button type="button" className="primary ocean-action" onClick={() => engine.current?.interact()}>{actionLabel}{!touch && <> <kbd>E</kbd></>}</button>}
          <div className="hotbar" role="toolbar" aria-label="Tools and blocks">
            {slots.map((s, i) => s ? (
              <button key={i} type="button" className={`hot-slot${s.key === current.key ? ' on' : ''}`} title={`${i + 1}: ${s.label}`} aria-pressed={s.key === current.key} aria-label={s.label}
                onClick={() => { setToolKey(s.key); if (s.key === 'net') engine.current?.swingNet(); if (s.key === 'sword') engine.current?.swingSword(); canvasRef.current?.focus(); }}>
                {s.color ? <span className="hot-block" style={{ background: s.color }} /> : <span aria-hidden>{s.icon}</span>}
                {s.count != null && <span className="hot-count">{s.count}</span>}
                <span className="hot-key">{i + 1}</span>
              </button>
            ) : (
              <button key={i} type="button" className="hot-slot empty" title={`${i + 1}: empty (click to fill it from your inventory)`} aria-label={`Slot ${i + 1}: empty`}
                onClick={() => { setInvTarget(i); setPanel('inv'); }}>
                <span className="hot-key">{i + 1}</span>
              </button>
            ))}
            <button type="button" className="hot-slot inv-open" title="Inventory (I): see all your items and arrange your hotbar" aria-label={`Inventory${offBar ? `, ${offBar} more items` : ''}`}
              onClick={() => { setInvTarget(null); setPanel('inv'); }}>
              <span aria-hidden>🎒</span>
              {offBar > 0 && <span className="hot-count">+{offBar}</span>}
            </button>
          </div>
          <div className="hotbar-hint small">
            {current.tool.kind === 'net' ? <>Swing your net with <kbd>Space</kbd> when a fish is in front of you</>
              : current.tool.kind === 'sword' ? <><kbd>Space</kbd> (or click) swings at sea beasts in front of you · {current.label.split(': ')[1]} (a swing that misses is free)</>
              : current.tool.kind === 'pick' ? ((inv.pickaxe ?? 0) > 0
                ? <>Click a block nearby to mine it, or <kbd>F</kbd> for the one in front · ⛏️ {inv.pickaxe} uses left</>
                : <>You need a pickaxe to mine: buy one in the shop (<kbd>C</kbd>)</>)
                : current.tool.kind === 'block' ? ((inv.hammer ?? 0) > 0
                  ? <>Click an empty space nearby to build, or <kbd>F</kbd> · 🔨 {inv.hammer} uses left{current.tool.item === 'beacon' && <> · <button type="button" className="linkish small hint-link" onClick={() => setGuide(true)}>📘 How beacons work</button></>}</>
                  : <>You need a hammer to build: buy one in the shop (<kbd>C</kbd>)</>)
                  : <>Click the sea floor near you to place your {current.label.split(' (')[0].toLowerCase()}</>}
          </div>
        </div>

        {hint && look && (
          <div className="ocean-hud center">
            <div className="ocean-hint">
              <strong>Welcome to your class ocean!</strong>
              {touch ? (
                <>
                  <span>🐟 Swim with the joystick (bottom left). The big button (bottom right) swings your net 🎣, or uses whatever you picked on the bar above it.</span>
                  <span>💬 When something glows near you (a sign, a chest, the Old Turtle…), tap the button that pops up above the bar.</span>
                  <span>🪣 🛠 📖 🎯 The buttons on the right: your bucket, crafting and the shop, your Fishpedia, and the class goals.</span>
                  <span>⛏️ Pick the pickaxe on the bar and tap a block to mine it; pick a block and tap an empty space to build.</span>
                </>
              ) : (<>
              <span>🐟 Swim with the arrow keys or WASD (or press and hold).</span>
              <span>🎣 <kbd>Space</kbd> swings your net: catch fish swimming in front of you. <kbd>P</kbd> opens your Fishpedia.</span>
              <span>🐢 Talk to the Old Turtle and the Ghost Captain (<kbd>E</kbd>): they give quests in the language you’re learning.</span>
              <span>⛏️ Buy a pickaxe to mine and a hammer to build (shop: <kbd>C</kbd>). They break after 15 blocks, so spend wisely!</span>
              <span>🛠 Craft new blocks and gear, and buy fish houses. <kbd>B</kbd> opens your bucket.</span>
              </>)}
              <span>🦈 Down in the caves and the deep, sea beasts bite! You have 3 ❤️. Lose them all and you’re sent back to the start (you keep your sand dollars). Buy a sword to fight back.</span>
              <span>🪙 Your teacher gives you sand dollars for good work and answers in class.</span>
              <button type="button" className="primary" onClick={() => { setHint(false); try { localStorage.setItem('ocean.sandbox.hinted', '1'); } catch { /* fine */ } canvasRef.current?.focus(); }}>Let’s go!</button>
            </div>
          </div>
        )}

        <div className="ocean-toasts" aria-live="polite">
          {toasts.map(t => (
            <div key={t.id} className={`ocean-toast${t.bad ? ' bad' : ''}`}>
              <strong>{t.title}{t.say && <button type="button" className="icon small" aria-label={`Say ${t.say}`} onClick={() => speak(t.say!, lang)}> 🔊</button>}</strong>
              {t.text && (t.lang
                ? <TranslatableText text={t.text} source={lang} target={native} className="ocean-toast-text" />
                : <span>{t.text}</span>)}
            </div>
          ))}
        </div>

        {error && <div className="ocean-hud center"><div className="notice bad">{error} <button className="small" onClick={() => void reload()}>Retry</button></div></div>}
        {!data && !error && <div className="ocean-hud center"><Loading /></div>}
      </div>

      {pediaOpen && <Fishpedia found={me?.pedia ?? []} lang={lang} onClose={() => { setPediaOpen(false); canvasRef.current?.focus(); }} />}

      {goalsOpen && (
        <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Class goals" onClick={() => setGoalsOpen(false)}>
          <div className="ocean-card tray" onClick={e => e.stopPropagation()}>
            <div className="row"><h3>🎯 This week’s class goals</h3><span className="spacer" /><button type="button" className="icon" aria-label="Close" onClick={() => setGoalsOpen(false)}>✕</button></div>
            <p className="small muted" style={{ margin: 0 }}>Everyone in the class counts. When a goal is reached, everyone gets its sand dollars.</p>
            <ClassGoals goals={goals} />
          </div>
        </div>
      )}

      {quiz?.kind === 'chest' && (
        <OceanQuiz icon={quiz.tier === 3 ? '👑' : '🧰'} title="A locked chest" prompt={quiz.term} lang={quiz.lang}
          question="The lock asks: what does this word mean? Get it right for bonus treasure."
          options={quiz.options}
          onPick={async o => { const q = quiz; setQuiz(null); await openTheChest(q.id, q.tier, o); canvasRef.current?.focus(); }}
          onClose={() => { const q = quiz; setQuiz(null); void openTheChest(q.id, q.tier); }} />
      )}

      {quiz?.kind === 'gate' && (
        <OceanQuiz icon="🗿" title="A sealed stone door" prompt={words.signs[10 + quiz.i]} lang={lang}
          question="Words are carved into the door. What do they say?"
          options={quiz.options} retryAfter={5}
          onPick={async o => {
            if (o !== englishSigns()[10 + quiz.i]) return false;
            const i = quiz.i;
            setQuiz(null);
            engine.current?.openGate(i);
            toast({ title: '🗿 The door rumbles open!', text: 'The Sunken Temple lies below. Beware…' });
            if (cid) void recordFind(cid, 'gate', `gate${i}`).catch(() => {});
            canvasRef.current?.focus();
            return true;
          }}
          onClose={() => { setQuiz(null); canvasRef.current?.focus(); }} />
      )}

      {npc && (
        <NpcDialog npc={npc} lang={lang} native={native} done={new Set(me?.quests ?? [])} inventory={inv} bucket={bucket}
          itemIcon={i => ITEM_ICON[i] ?? (blockByItem(i) ? '▪️' : '•')} itemName={itemName}
          onGive={async quest => {
            if (!cid) return;
            try {
              const reward = await doQuest(cid, quest);
              toast({ title: '📜 Quest done!', text: Object.entries(reward).map(([k, v]) => `${ITEM_ICON[k] ?? (blockByItem(k) ? '▪️' : '•')} ${itemName(k)} × ${v}`).join('  ') });
              await reload();
              void refreshGoals();
            } catch (err) { toast({ title: err instanceof Error ? err.message : 'That didn’t work.', bad: true }); }
          }}
          onClose={() => { setNpc(null); canvasRef.current?.focus(); }} />
      )}

      {beacon && engine.current && (
        <HabitatPanel
          status={engine.current.habitat(beacon.tx, beacon.ty)}
          habitat={(data?.habitats ?? []).find(h => h.tx === beacon.tx && h.ty === beacon.ty) ?? null}
          onClose={() => { setBeacon(null); canvasRef.current?.focus(); }}
          onGuide={() => setGuide(true)}
          onRelease={(id, species) => letGo(species, () => void run(() => releaseResident(id), `You let the ${speciesById(species).name} go. 🌊 Your habitat can attract another.`))}
        />
      )}

      {room && (
        <HouseRoom
          house={room}
          editable={room.mine}
          prices={decorPrices}
          balance={room.mine ? balance : undefined}
          onSave={async interior => { const b = await decorateHouse(room.id, interior); setBalance(b); await reload(); toast({ title: 'Room saved! 🏡' }); }}
          onClose={() => { setRoomId(null); canvasRef.current?.focus(); }}
        />
      )}

      {sign !== null && (
        <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Sign" onClick={() => setSign(null)}>
          <div className="ocean-card sign-card" onClick={e => e.stopPropagation()}>
            <div className="small muted">Tap a word to translate it</div>
            <div className="sign-text">
              <TranslatableText text={words.signs[sign]} source={lang} target={native} onSave={onSave} savedKeys={savedKeys} />
            </div>
            <button type="button" className="primary" onClick={() => { setSign(null); canvasRef.current?.focus(); }}>Keep swimming</button>
          </div>
        </div>
      )}

      {letGoAsk && (
        <div className="ocean-modal" style={{ zIndex: 60 }} role="dialog" aria-modal="true" aria-label="Let this fish go?" onClick={() => setLetGoAsk(null)}>
          <div className="ocean-card tray" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>🌊 Let your {speciesById(letGoAsk.species).name} go?</h3>
            <p className="small">It swims away for good (it stays in your Fishpedia).</p>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setLetGoAsk(null)} autoFocus>Keep it</button>
              <button type="button" className="primary" onClick={() => { const go = letGoAsk.go; setLetGoAsk(null); go(); }}>Let it go</button>
            </div>
          </div>
        </div>
      )}
      {guide && <BeaconGuide onClose={() => { setGuide(false); canvasRef.current?.focus(); }} />}

      {panel === 'inv' && (
        <InventoryPanel items={items} hotbar={hotbar} onHotbar={setHotbar} current={current.key} target={invTarget}
          onUse={k => { setToolKey(k); if (!hotbar.includes(k)) { const free = hotbar.indexOf(null); if (free >= 0) setHotbar(place(hotbar, k, free)); } }}
          onClose={() => { setPanel(null); setInvTarget(null); canvasRef.current?.focus(); }} />
      )}

      {panel === 'craft' && (
        <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Craft and shop" onClick={() => setPanel(null)}>
          <div className="ocean-card tray wide" onClick={e => e.stopPropagation()}>
            <div className="row"><h3>Craft & shop</h3><span className="spacer" /><span className="ocean-chip sand">🪙 {sandDollars(balance)}</span><button type="button" className="icon" aria-label="Close" onClick={() => setPanel(null)}>✕</button></div>
            <div className="inv-row">
              {Object.entries(inv).filter(([, n]) => n > 0).length === 0
                ? <span className="small muted">Nothing yet. Mine blocks with the pickaxe and find pearls.</span>
                : Object.entries(inv).filter(([, n]) => n > 0).map(([k, n]) => (
                  <span key={k} className="inv-chip">
                    {blockByItem(k) ? <span className="hot-block small" style={{ background: blockByItem(k)!.color }} /> : <span aria-hidden>{ITEM_ICON[k] ?? '•'}</span>}
                    {TOOLS.has(k) ? `${itemName(k)}: ${n} uses left` : `${itemName(k)} × ${n}`}
                  </span>
                ))}
            </div>
            {(['craft', 'shop'] as const).map(kind => (
              <div key={kind}>
                <h4 className="recipe-head">{kind === 'craft' ? '🛠 Craft' : '🛒 Shop: tools and fish houses'}</h4>
                <div className="recipe-list">
                  {recipes.filter(r => r.kind === kind).map(r => {
                    const owned = (r.output === 'drill' || r.output === 'golden_net') && (inv[r.output] ?? 0) > 0;
                    const can = !owned && balance >= r.cost && has(r.inputs);
                    return (
                      <div key={r.id} className="recipe">
                        <span className="recipe-icon" aria-hidden>{blockByItem(r.output) ? <span className="hot-block" style={{ background: blockByItem(r.output)!.color }} /> : ITEM_ICON[r.output] ?? '✨'}</span>
                        <span className="grow">
                          <strong>{r.name}{r.output_qty > 1 && !TOOLS.has(r.output) ? ` × ${r.output_qty}` : ''}</strong>
                          <span className="small muted">{r.blurb}{r.output === 'beacon' && <> · <button type="button" className="linkish small" onClick={() => setGuide(true)}>📘 How it works</button></>}</span>
                          <span className="small">
                            {Object.entries(r.inputs).map(([k, n]) => (
                              <span key={k} className={(inv[k] ?? 0) >= n ? 'need ok' : 'need'}>{n} {itemName(k).toLowerCase()}</span>
                            ))}
                            <span className={balance >= r.cost ? 'need ok' : 'need'}>🪙 {r.cost}</span>
                          </span>
                        </span>
                        <button type="button" className={can ? 'primary small' : 'small'} disabled={!can || busy}
                          onClick={() => void run(async () => { const res = await craft(active.id, r.id); setBalance(res.balance); },
                            TOOLS.has(r.output) ? `New ${r.name.toLowerCase()}! ${r.output_qty} more blocks to ${r.output === 'pickaxe' ? 'mine' : 'build'}.`
                              : kind === 'shop' ? `You bought a ${r.name.toLowerCase()}! ${isHouse(r.output) ? 'Pick it on your hotbar and click the sea floor near you.' : r.output === 'sword' || r.output === 'trident' ? 'Pick it on your hotbar and swing with Space.' : 'It’s on your hotbar.'}` : `Crafted ${r.name.toLowerCase()}!`)}>
                          {owned ? 'Owned' : kind === 'shop' ? 'Buy' : 'Craft'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {panel === 'bucket' && (
        <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Bucket and houses" onClick={() => setPanel(null)}>
          <div className="ocean-card tray wide" onClick={e => e.stopPropagation()}>
            <div className="row"><h3>🪣 Bucket & houses</h3><span className="spacer" /><button type="button" className="icon" aria-label="Close" onClick={() => setPanel(null)}>✕</button></div>
            <h4 className="recipe-head">In your bucket ({bucket.length} of {BUCKET_SIZE})</h4>
            {bucket.length >= BUCKET_SIZE && <div className="notice small">Your bucket is full, so your net can’t catch more. Move fish into a house to make room.</div>}
            {bucket.length === 0 ? <p className="small muted">No fish waiting. Swim around and press <kbd>Space</kbd> to catch some!</p> : (
              <div className="tray-list">
                {bucket.map(f => (
                  <div key={f.id} className="tray-row">
                    <img src={speciesImage(f.species)} alt="" />
                    <span className="grow"><strong>{speciesById(f.species).name}</strong> <RarityTag id={f.species} /></span>
                    {myHouses.length === 0 ? <span className="small muted">Needs a house</span> : myHouses.map(h => (
                      <button key={h.id} type="button" className="small" disabled={busy || h.fish.length >= HOUSES[h.kind].capacity}
                        onClick={() => void run(() => moveFish(f.id, h.id))}>
                        → {HOUSES[h.kind].name} ({h.fish.length}/{HOUSES[h.kind].capacity})
                      </button>
                    ))}
                    <button type="button" className="small" disabled={busy} title="Let it go back into the ocean"
                      onClick={() => letGo(f.species, () => void run(() => releaseFish(f.id), `You let your ${speciesById(f.species).name} go. 🌊`))}>🌊 Let go</button>
                  </div>
                ))}
              </div>
            )}
            <h4 className="recipe-head">Your houses</h4>
            {myHouses.length === 0 ? <p className="small muted">No houses yet. Buy one in the shop (<kbd>C</kbd>) with sand dollars, pick it in your hotbar, and click anywhere on the sea floor near you to put it there.</p> : (
              <div className="tray-list">
                {myHouses.map(h => (
                  <div key={h.id} className="house-row">
                    <div className="row">
                      <strong>{ITEM_ICON[h.kind]} {HOUSES[h.kind].name}</strong>
                      <span className="small muted">{h.fish.length} of {HOUSES[h.kind].capacity} fish</span>
                      <span className="spacer" />
                      <button type="button" className="small" disabled={busy} onClick={() => void run(() => pickUpHouse(h.id), 'House picked up. Its fish are back in your bucket.')}>Pick up</button>
                    </div>
                    <div className="house-fish">
                      {h.fish.map(f => (
                        <span key={f.id} className="inv-chip">
                          <img src={speciesImage(f.species)} alt="" /> {speciesById(f.species).name}
                          <button type="button" className="icon" aria-label="Back to bucket" title="Back to your bucket" disabled={busy} onClick={() => void run(() => moveFish(f.id, null))}>↩</button>
                          <button type="button" className="icon" aria-label={`Let the ${speciesById(f.species).name} go`} title="Let it go back into the ocean" disabled={busy}
                            onClick={() => letGo(f.species, () => void run(() => releaseFish(f.id), `You let your ${speciesById(f.species).name} go. 🌊`))}>🌊</button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {data && (builder || !look) && (
        <FishBuilder
          initial={look ?? randomLook()}
          required={!look}
          onCancel={() => setBuilder(false)}
          onSave={async l => {
            if (!session) throw new Error('Not signed in.');
            await saveLook(session.user.id, l);
            setBuilder(false);
            await reload();
            canvasRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}
